import { useEffect, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { Icon } from '../icons';
import { getStorageOrThrow } from '../../lib/firebase';
import type { JobDoc, JobStatus } from '../../lib/types';
import type { JobListItem } from '../../hooks/useMyJobs';

interface Props {
  jobs: JobListItem[];
  loading: boolean;
  onOpenPreview: (jobId: string, job: JobDoc) => void;
  onOpenPurchase: (jobId: string, job: JobDoc) => void;
  onOpenSuccess: (jobId: string, job: JobDoc) => void;
  onContinueDraft: (jobId: string, job: JobDoc) => void;
  onContinueProcessing: (jobId: string, job: JobDoc) => void;
  onRemoveProject: (jobId: string) => Promise<void>;
  onNewMovie: () => void;
}

function statusLabel(status: JobStatus): string {
  switch (status) {
    case 'paid':
      return 'נרכש';
    case 'ready':
      return 'מוכן לרכישה';
    case 'failed':
      return 'אירעה תקלה';
    case 'draft':
      return 'טיוטה';
    case 'queued':
    case 'scripting':
    case 'tts':
    case 'rendering':
      return 'מעבדים…';
    default:
      return status;
  }
}

function eventLabel(eventType: JobDoc['eventType']): string {
  switch (eventType) {
    case 'barmitzvah':
      return 'בר/בת מצווה';
    case 'wedding':
      return 'חתונה';
    case 'birthday':
      return 'יום הולדת';
    case 'anniversary':
      return 'יום נישואין';
    case 'family':
      return 'משפחה';
    default:
      return 'אירוע';
  }
}

/** Same primary action as the main button on the card (card tap = this). */
function activateProject(
  id: string,
  job: JobDoc,
  handlers: {
    onOpenPreview: Props['onOpenPreview'];
    onOpenPurchase: Props['onOpenPurchase'];
    onOpenSuccess: Props['onOpenSuccess'];
    onContinueDraft: Props['onContinueDraft'];
    onContinueProcessing: Props['onContinueProcessing'];
  },
): void {
  const paid = job.status === 'paid';
  const ready = job.status === 'ready';
  const processing = ['queued', 'scripting', 'tts', 'rendering'].includes(job.status);
  const failed = job.status === 'failed';
  const draft = job.status === 'draft';

  if (paid) handlers.onOpenSuccess(id, job);
  else if (ready) handlers.onOpenPreview(id, job);
  else if (processing || failed) handlers.onContinueProcessing(id, job);
  else if (draft) handlers.onContinueDraft(id, job);
}

/** Up to three Storage paths for the stacked preview (same layout as welcome hero). */
function slotsForJob(job: JobDoc): (string | null)[] {
  const sorted = [...(job.photos ?? [])].sort((a, b) => a.order - b.order);
  const s: (string | null)[] = [null, null, null];
  if (sorted.length >= 3) {
    s[0] = sorted[0]!.path;
    s[1] = sorted[1]!.path;
    s[2] = sorted[2]!.path;
  } else if (sorted.length === 2) {
    s[0] = sorted[0]!.path;
    s[1] = sorted[1]!.path;
  } else if (sorted.length === 1) {
    s[1] = sorted[0]!.path;
  } else if (job.assets?.thumbPath) {
    s[1] = job.assets.thumbPath;
  }
  return s;
}

async function storageUrlWithRetries(storagePath: string): Promise<string | null> {
  const storage = getStorageOrThrow();
  const r = ref(storage, storagePath);
  const delays = [0, 600, 1600];
  for (const d of delays) {
    if (d) await new Promise((res) => setTimeout(res, d));
    try {
      return await getDownloadURL(r);
    } catch {
      /* retry */
    }
  }
  return null;
}

export function ProjectsScreen({
  jobs,
  loading,
  onOpenPreview,
  onOpenPurchase,
  onOpenSuccess,
  onContinueDraft,
  onContinueProcessing,
  onRemoveProject,
  onNewMovie,
}: Props) {
  /** Per job: up to 3 resolved download URLs (parallel to slotsForJob order). */
  const [previewTrios, setPreviewTrios] = useState<Record<string, (string | null)[]>>({});
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handlers = {
    onOpenPreview,
    onOpenPurchase,
    onOpenSuccess,
    onContinueDraft,
    onContinueProcessing,
  };

  useEffect(() => {
    let cancelled = false;
    async function loadTrios() {
      const next: Record<string, (string | null)[]> = {};
      for (const { id, job } of jobs) {
        const paths = slotsForJob(job);
        const urls = await Promise.all(
          paths.map((p) => (p ? storageUrlWithRetries(p) : Promise.resolve(null))),
        );
        if (!cancelled) next[id] = urls;
      }
      if (!cancelled) setPreviewTrios((prev) => ({ ...prev, ...next }));
    }
    void loadTrios();
    return () => {
      cancelled = true;
    };
  }, [jobs]);

  async function handleRemove(id: string, job: JobDoc) {
    const label = `${eventLabel(job.eventType)} · ${new Date(job.createdAt).toLocaleDateString('he-IL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })}`;
    const ok = window.confirm(
      `למחוק את הסרט מהרשימה? הפעולה בלתי הפיכה (כולל קבצים שהועלו לפרויקט זה).\n${label}`,
    );
    if (!ok) return;
    setRemovingId(id);
    try {
      await onRemoveProject(id);
    } finally {
      setRemovingId(null);
    }
  }

  function cardKeyActivate(id: string, job: JobDoc) {
    activateProject(id, job, handlers);
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <div style={{ width: 38 }} />
        <div className="step-counter">הסרטים שלי</div>
        <div style={{ width: 38 }} />
      </div>
      <div className="screen-scroll">
        <div className="eyebrow" style={{ marginTop: 18 }}>
          רגעים
        </div>
        <h1 className="h1" style={{ fontSize: 26 }}>
          הסרטונים שלכם
        </h1>
        <p className="lede">
          בוחרים סרט קיים להמשך רכישה או צפייה, או יוצרים סרט חדש מההתחלה.
        </p>

        <button type="button" className="btn-primary" onClick={onNewMovie} style={{ marginTop: 8 }}>
          <Icon.Sparkles size={18} />
          סרט חדש
        </button>

        {loading && jobs.length === 0 ? (
          <p className="lede" style={{ marginTop: 24, textAlign: 'center' }}>
            טוענים…
          </p>
        ) : jobs.length === 0 ? (
          <p className="lede" style={{ marginTop: 24, textAlign: 'center', opacity: 0.85 }}>
            עדיין אין סרטונים. לחצו &quot;סרט חדש&quot; כדי להתחיל.
          </p>
        ) : (
          <div className="projects-grid projects-grid-library">
            {jobs.map(({ id, job }) => {
              const trio = previewTrios[id] ?? [null, null, null];
              const updatedTs = job.updatedAt ?? job.createdAt;
              const updatedStr = new Date(updatedTs).toLocaleDateString('he-IL', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              });
              const paid = job.status === 'paid';
              const ready = job.status === 'ready';
              const processing = ['queued', 'scripting', 'tts', 'rendering'].includes(job.status);
              const failed = job.status === 'failed';
              const draft = job.status === 'draft';

              return (
                <div
                  key={id}
                  role="button"
                  tabIndex={0}
                  className="card project-library-card"
                  onClick={() => cardKeyActivate(id, job)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      cardKeyActivate(id, job);
                    }
                  }}
                >
                  <button
                    type="button"
                    className="btn-ghost"
                    aria-label="מחק סרט"
                    disabled={removingId === id}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleRemove(id, job);
                    }}
                    style={{
                      position: 'absolute',
                      top: 6,
                      insetInlineEnd: 6,
                      padding: 6,
                      zIndex: 5,
                      color: 'var(--ink-soft)',
                      cursor: 'pointer',
                    }}
                    title="מחק סרט"
                  >
                    <Icon.Trash size={18} />
                  </button>

                  <div className="project-card-stack">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="photo">
                        {trio[i] ? (
                          <img src={trio[i]!} alt="" draggable={false} />
                        ) : (
                          <div className="ph-placeholder" style={{ fontSize: 9 }}>
                            &nbsp;
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="project-card-meta">
                    <div className="project-card-type">{eventLabel(job.eventType)}</div>
                    <div className="project-card-updated">עודכן {updatedStr}</div>
                    <div className="project-card-status">{statusLabel(job.status)}</div>
                  </div>

                  <div className="project-card-actions">
                    {paid && (
                      <button
                        type="button"
                        className="btn-primary project-card-wide"
                        style={{ padding: '8px 14px', fontSize: 13 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenSuccess(id, job);
                        }}
                      >
                        הורדה / פרטים
                      </button>
                    )}
                    {ready && (
                      <>
                        <button
                          type="button"
                          className="btn-primary project-card-wide"
                          style={{ padding: '8px 14px', fontSize: 13 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenPreview(id, job);
                          }}
                        >
                          צפייה
                        </button>
                        <button
                          type="button"
                          className="btn-secondary project-card-wide"
                          style={{ padding: '8px 14px', fontSize: 13 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenPurchase(id, job);
                          }}
                        >
                          רכישה
                        </button>
                      </>
                    )}
                    {(processing || failed) && (
                      <button
                        type="button"
                        className="btn-primary project-card-wide"
                        style={{ padding: '8px 14px', fontSize: 13 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onContinueProcessing(id, job);
                        }}
                      >
                        {failed ? 'פרטים' : 'המשך'}
                      </button>
                    )}
                    {draft && (
                      <button
                        type="button"
                        className="btn-primary project-card-wide"
                        style={{ padding: '8px 14px', fontSize: 13 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onContinueDraft(id, job);
                        }}
                      >
                        המשך עריכה
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
